import { Injectable, inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Service pour gérer les abonnements aux notifications push via le service worker.
 * Permet aux notifications de s'afficher même lorsque l'application n'est pas ouverte.
 */
@Injectable({
    providedIn: 'root'
})
export class PushSubscriptionService {
    private swPush = inject(SwPush);
    private http = inject(HttpClient);

    private readonly apiUrl = `${environment.apiBaseUrl}notification/push-subscription`;

    /**
     * Demande l'abonnement aux notifications push et l'enregistre auprès du backend.
     * Utilise le service worker pour que les notifications fonctionnent en arrière-plan.
     */
    subscribeToPushNotifications(): Observable<boolean> {
        if (!this.swPush.isEnabled) {
            return of(false);
        }

        const vapidPublicKey = (environment as { vapidPublicKey?: string }).vapidPublicKey;
        if (!vapidPublicKey) {
            console.warn('VAPID public key not configured. Push notifications disabled.');
            return of(false);
        }

        return from(
            this.swPush.requestSubscription({ serverPublicKey: vapidPublicKey })
        ).pipe(
            switchMap((subscription) => {
                const subscriptionJson = subscription.toJSON();
                return this.http.post<void>(this.apiUrl, subscriptionJson).pipe(
                    switchMap(() => of(true)),
                    catchError((err) => {
                        console.error('Failed to register push subscription:', err);
                        return of(false);
                    })
                );
            }),
            catchError((err) => {
                console.error('Failed to subscribe to push notifications:', err);
                return of(false);
            })
        );
    }

    /**
     * Désabonne l'utilisateur des notifications push.
     */
    unsubscribeFromPushNotifications(): Observable<boolean> {
        if (!this.swPush.isEnabled) {
            return of(false);
        }

        return from(this.swPush.unsubscribe()).pipe(
            switchMap(() => this.http.delete<void>(this.apiUrl).pipe(
                switchMap(() => of(true)),
                catchError(() => of(true)) // Unsubscribe locally even if backend fails
            )),
            catchError(() => of(false))
        );
    }

    /**
     * Indique si le service worker et les notifications push sont disponibles.
     */
    get isPushEnabled(): boolean {
        return this.swPush.isEnabled;
    }
}
